import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useBranding } from "@/branding/BrandingContext";
import CardDetailOverlay from "@/components/detail/CardDetailOverlay";
import EditorialMain from "@/components/pulse/EditorialMain";
import PulseHeader from "@/components/pulse/PulseHeader";
import PulsePageShell from "@/components/pulse/PulsePageShell";
import QueryState from "@/components/states/QueryState";
import { usePulseProfiles, useResolvedEdition, useWeeklyFeatures } from "@/hooks/usePulse";
import { hasUnsubscribeIntent } from "@/lib/unsubscribe";
import type { PulseCard } from "@/types";

export default function Home() {
  const { fecha } = useParams<{ fecha?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setBranding } = useBranding();
  const [selectedCard, setSelectedCard] = useState<PulseCard | null>(null);
  const [selectedCardBg, setSelectedCardBg] = useState("#fff5ec");
  const [unsubscribeIntent, setUnsubscribeIntent] = useState(() =>
    hasUnsubscribeIntent(window.location.search),
  );

  const handleSelectCard = (card: PulseCard, sourceBg?: string) => {
    setSelectedCard(card);
    setSelectedCardBg(sourceBg ?? "#fff5ec");
  };
  const [selectedProfileSlug, setSelectedProfileSlug] = useState<string | undefined>(undefined);
  const requestedProfileSlug = searchParams.get("profile")?.trim() || undefined;

  const clearUnsubscribeQuery = useCallback(() => {
    if (!hasUnsubscribeIntent(searchParams.toString())) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("unsubscribe");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleUnsubscribeIntentConsumed = useCallback(() => {
    setUnsubscribeIntent(false);
    clearUnsubscribeQuery();
  }, [clearUnsubscribeQuery]);

  useEffect(() => {
    if (hasUnsubscribeIntent(searchParams.toString())) {
      setUnsubscribeIntent(true);
    }
  }, [searchParams]);

  const profilesQuery = usePulseProfiles(selectedProfileSlug);
  const resolved = useResolvedEdition(fecha, selectedProfileSlug);
  const weeklyFeaturesQuery = useWeeklyFeatures(3, selectedProfileSlug);

  const { edition, editions, isLoading, isError, error, isEmpty, resolvedDate } = resolved;

  useEffect(() => {
    const payload = profilesQuery.data;
    if (!payload) return;

    const hasSelected =
      !!selectedProfileSlug && payload.profiles.some((profile) => profile.slug === selectedProfileSlug);

    if (!hasSelected) {
      const requested = requestedProfileSlug
        ? payload.profiles.find((profile) => profile.slug === requestedProfileSlug)?.slug
        : undefined;
      setSelectedProfileSlug(requested ?? payload.active_slug);
    }
  }, [profilesQuery.data, selectedProfileSlug, requestedProfileSlug]);

  useEffect(() => {
    if (edition) setBranding(edition.profile_slug, edition.brand);
  }, [edition, setBranding]);

  useEffect(() => {
    setSelectedCard(null);
    setSelectedCardBg("#fff5ec");
  }, [edition?.fecha]);

  useEffect(() => {
    if (!fecha || !resolvedDate || fecha === resolvedDate) return;
    navigate(`/dia/${resolvedDate}`, { replace: true });
  }, [fecha, resolvedDate, navigate]);

  const selectedDate = resolvedDate ?? fecha ?? editions[0]?.fecha ?? "";
  const query = { isLoading, isError, error };
  const profileSwitchProps = {
    availableProfiles: profilesQuery.data?.profiles ?? [],
    activeProfileSlug: selectedProfileSlug ?? profilesQuery.data?.active_slug,
    onChangeProfile: setSelectedProfileSlug,
  };

  return (
    <PulsePageShell
      header={
        !isError ? (
          <PulseHeader editions={editions} selectedDate={selectedDate} />
        ) : null
      }
      unsubscribeIntent={unsubscribeIntent}
      onUnsubscribeIntentConsumed={handleUnsubscribeIntentConsumed}
    >
      {isLoading && !edition ? (
        <EditorialMain
          cards={[]}
          weeklyFeatures={[]}
          selectedDate={selectedDate}
          weeklyLoading
          loading
          {...profileSwitchProps}
          onSelectCard={() => {}}
        />
      ) : (
        <QueryState
          query={query}
          isEmpty={isEmpty}
          emptyMessage="No hay ediciones publicadas todavía."
        >
          {edition && (
            <EditorialMain
              cards={edition.cards ?? []}
              weeklyFeatures={weeklyFeaturesQuery.data ?? []}
              selectedDate={selectedDate}
              weeklyLoading={weeklyFeaturesQuery.isLoading}
              {...profileSwitchProps}
              onSelectCard={handleSelectCard}
            />
          )}
        </QueryState>
      )}

      <CardDetailOverlay
        card={selectedCard}
        sourceCardBg={selectedCardBg}
        onClose={() => setSelectedCard(null)}
      />
    </PulsePageShell>
  );
}
